import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";

import { useGetOrCreateUserProgression } from "@/api/generated";
import {
  UserProgressionStateModel,
  WorkflowType,
} from "@/api/generated/models";
import { useAuthState } from "@/hooks/useAuthState";
import { usePartnerIntent } from "@/hooks/usePartnerIntent";

export const FLOW_NAME = {
  [WorkflowType.rx_renewal]: "ai_doctor_rx_renewal",
  [WorkflowType.diagnosis]: "ai_doctor_diagnosis",
} as const;

const progressionQueryKey = (type: WorkflowType) => [
  "aiDoctorProgression",
  type,
];

export const useInvalidateAIDoctorProgression = () => {
  const queryClient = useQueryClient();
  return (type: WorkflowType) =>
    queryClient.removeQueries({ queryKey: progressionQueryKey(type) });
};

/**
 * Fetches (or resumes) the AI Doctor progression for the given workflow type.
 * Safe to call early (e.g. on the landing page) to pre-warm the cache so the
 * sidebar opens instantly with no loading state.
 */
export const useAIDoctorProgression = (type: WorkflowType) => {
  const { isLoading, isInitializing } = useAuthState();
  const isAuthReady = !isLoading && !isInitializing;
  const queryClient = useQueryClient();
  const { getLocalPartnerIntent } = usePartnerIntent();
  const { mutate, isPending, isIdle, isError } =
    useGetOrCreateUserProgression();

  const data = queryClient.getQueryData<UserProgressionStateModel>(
    progressionQueryKey(type),
  );

  const fetchProgression = useCallback(async () => {
    const partnerIntent = await getLocalPartnerIntent();
    mutate(
      {
        data: {
          flow_name: FLOW_NAME[type],
          ...(partnerIntent && { metadata: { partner_intent: partnerIntent } }),
        },
      },
      {
        onSuccess: (result) => {
          queryClient.setQueryData(progressionQueryKey(type), result);
        },
      },
    );
  }, [mutate, type, queryClient, getLocalPartnerIntent]);

  useEffect(() => {
    if (!isAuthReady || data) return;
    fetchProgression();
  }, [isAuthReady, data, fetchProgression]);

  return {
    data,
    isPending: (isIdle || isPending) && isAuthReady && !data,
    isError,
    retry: fetchProgression,
  };
};
